import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Interactive map of every pinnable startup-ecosystem org.
 *
 * Leaflet is imported dynamically so its CSS + tile bundle doesn't end up in
 * the initial bundle. The component owns the map lifecycle directly (rather
 * than using react-leaflet) so we can keep zero extra deps beyond leaflet
 * itself — the map we render has enough interactions without the wrapper.
 */

interface MapOrg {
  id: string;
  name: string;
  short_title?: string | null;
  category?: string | null;
  state?: string | null;
  summary?: string | null;
  application_url?: string | null;
  source_url?: string | null;
  audience?: string | null;
  benefit_type?: string | null;
  lat: number;
  lng: number;
}

type LeafletModule = typeof import("leaflet");

interface Props {
  onOpenScheme: (id: string) => void;
  /** When true, render map-only (no side list) and fill the container. */
  standalone?: boolean;
}

export function EcosystemMap({ onOpenScheme, standalone = false }: Props) {
  const [orgs, setOrgs]       = useState<MapOrg[] | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [activeId, setActive] = useState<string | null>(null);
  const containerRef          = useRef<HTMLDivElement>(null);
  const leafletRef            = useRef<LeafletModule | null>(null);
  const mapRef                = useRef<import("leaflet").Map | null>(null);
  const markerRefs            = useRef<Record<string, import("leaflet").Marker>>({});

  // Visibility gate — only fetch + mount Leaflet when the container
  // scrolls into view. Saves ~350ms of LCP on slow networks because the
  // hero paints without blocking on the /api/ecosystem/map request or
  // the 70KB leaflet chunk.
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!containerRef.current) return;
    // If IntersectionObserver isn't available, render eagerly.
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) { setInView(true); io.disconnect(); } },
      { rootMargin: "200px" },
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    fetch(import.meta.env.VITE_API_BASE
      ? `${import.meta.env.VITE_API_BASE}/api/ecosystem/map`
      : (typeof window !== "undefined" && /(^|\.)ind-startups\.aisarva\.com$/.test(window.location.hostname)
          ? "https://api-ind-startups.aisarva.com/api/ecosystem/map"
          : "/api/ecosystem/map"))
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setOrgs(d.items || []); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [inView]);

  // Lazy import Leaflet once we have data.
  useEffect(() => {
    if (!orgs || !containerRef.current || mapRef.current) return;
    let cancelled = false;
    (async () => {
      const L = await import("leaflet");
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !containerRef.current) return;
      leafletRef.current = L;
      const map = L.map(containerRef.current, {
        center: [22.5, 79.0],          // roughly centered on India
        zoom: 5,
        scrollWheelZoom: true,
        wheelPxPerZoomLevel: 90,
        zoomControl: true,
        attributionControl: true,
        zoomSnap: 0.25,
        zoomDelta: 0.5,
        doubleClickZoom: true,
        touchZoom: true,
      });
      L.tileLayer(
        "https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png",
        {
          maxZoom: 18,
          subdomains: "abcd",
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> ' +
            'contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        },
      ).addTo(map);

      mapRef.current = map;
      orgs.forEach((o) => addMarker(L, map, o));
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRefs.current = {};
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs]);

  const addMarker = (
    L: LeafletModule,
    map: import("leaflet").Map,
    org: MapOrg,
  ) => {
    const color = categoryColor(org.category);
    const icon = L.divIcon({
      className: "",
      iconSize:  [22, 22],
      iconAnchor: [11, 11],
      html: `
        <div style="position:relative;width:22px;height:22px;">
          <span style="position:absolute;inset:0;border-radius:9999px;background:${color};opacity:.25;
                       animation:map-pin-pulse 2.6s cubic-bezier(.5,0,0,1) infinite"></span>
          <span style="position:absolute;inset:4px;border-radius:9999px;background:${color};
                       box-shadow:0 2px 6px rgba(15,23,42,.25);"></span>
        </div>`,
    });
    const marker = L.marker([org.lat, org.lng], { icon, title: org.name }).addTo(map);
    marker.bindPopup(buildPopup(org), {
      offset: L.point(0, -4), maxWidth: 280, autoClose: false, closeButton: true,
    });
    marker.on("click", () => setActive(org.id));
    marker.on("popupopen", () => {
      const root = marker.getPopup()?.getElement();
      if (!root) return;
      root.querySelector("[data-open-scheme]")?.addEventListener("click", (e) => {
        e.preventDefault();
        onOpenScheme(org.id);
        marker.closePopup();
      });
    });
    markerRefs.current[org.id] = marker;
  };

  const activeOrg = useMemo(
    () => (orgs ?? []).find((o) => o.id === activeId) ?? null,
    [orgs, activeId],
  );

  const flyTo = (org: MapOrg) => {
    if (!mapRef.current) return;
    mapRef.current.flyTo([org.lat, org.lng], 10, { duration: 0.8 });
    markerRefs.current[org.id]?.openPopup();
    setActive(org.id);
  };

  if (error) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-4">
        Couldn't load the map: {error}
      </div>
    );
  }

  if (standalone) {
    return (
      <div className="relative w-full h-full">
        <div ref={containerRef} className="absolute inset-0 z-0" />
        {!orgs && (
          <div className="absolute inset-0 flex items-center justify-center text-cream-100/70 text-sm bg-plum-900/60 font-mono uppercase tracking-[0.25em] text-[11px]">
            <span className="oracle-pulse mr-2" />
            reading map…
          </div>
        )}
        <Legend />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-4">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-plum-900/60 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]">
        <div ref={containerRef} className="w-full h-[540px] z-0" />
        {!orgs && (
          <div className="absolute inset-0 flex items-center justify-center text-cream-100/70 text-sm bg-plum-900/60 font-mono uppercase tracking-[0.25em] text-[11px]">
            <span className="oracle-pulse mr-2" />
            reading map…
          </div>
        )}
        <Legend />
      </div>

      <aside className="bg-plum-900/60 border border-white/10 rounded-2xl p-4 max-h-[540px] overflow-y-auto backdrop-blur">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
            Orgs on the map
          </h3>
          <span className="text-xs font-mono text-cream-100/55">{orgs?.length ?? 0}</span>
        </div>
        <ul className="space-y-1.5">
          {(orgs ?? []).map((o) => (
            <li key={o.id}>
              <button
                onClick={() => flyTo(o)}
                className={`w-full text-left rounded-lg px-2 py-1.5 flex items-start gap-2 transition border
                            ${activeId === o.id
                              ? "bg-amber-500/15 border-amber-400/40"
                              : "border-transparent hover:bg-white/[0.04]"}`}
              >
                <span
                  className="shrink-0 w-2 h-2 rounded-full mt-1.5"
                  style={{ background: categoryColor(o.category), boxShadow: `0 0 8px ${categoryColor(o.category)}` }}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate text-cream-50">{o.name}</span>
                  <span className="block text-[11px] text-cream-100/50 truncate">
                    {[o.state, o.category].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {activeOrg && (
          <button
            onClick={() => onOpenScheme(activeOrg.id)}
            className="mt-4 w-full bg-cream-50 hover:bg-amber-300 text-plum-950 text-sm font-semibold rounded-xl py-2 transition"
          >
            Open “{activeOrg.short_title || activeOrg.name.slice(0, 22)}” details →
          </button>
        )}
      </aside>
    </div>
  );
}

function Legend() {
  const items: Array<[string, string]> = [
    ["#14b8a6", "State Mission"],
    ["#f97316", "Incubator / Accelerator"],
    ["#8b5cf6", "Seed / Agency"],
    ["#ef4444", "VC Fund"],
    ["#f59e0b", "Angel Network"],
    ["#ec4899", "TiE Chapter"],
    ["#22d3ee", "Coworking+Community"],
    ["#a3e635", "Community Collective"],
    ["#64748b", "Portal"],
  ];
  return (
    <div
      className="absolute bottom-3 left-3 backdrop-blur rounded-lg px-3 py-2 text-[11px] shadow-sm z-[400]"
      style={{ background: "rgba(250,246,236,0.94)", border: "1px solid rgba(26,12,16,0.12)", color: "#1a0c10" }}
    >
      <div
        className="font-semibold uppercase tracking-widest text-[10px] mb-1"
        style={{ color: "#6a5a56" }}
      >
        Legend
      </div>
      {items.map(([c, label]) => (
        <div key={label} className="flex items-center gap-2" style={{ color: "#1a0c10" }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function categoryColor(category?: string | null): string {
  const c = (category || "").toLowerCase();
  if (c.includes("vc fund"))                         return "#ef4444"; // VC red
  if (c.includes("angel"))                           return "#f59e0b"; // angel amber
  if (c.includes("accelerator"))                     return "#f97316"; // accelerator orange
  if (c.includes("tie"))                             return "#ec4899"; // TiE pink
  if (c.includes("coworking"))                       return "#22d3ee"; // cyan
  if (c.includes("community"))                       return "#a3e635"; // lime
  if (c.includes("nasscom"))                         return "#a78bfa"; // violet
  if (c.includes("state") && c.includes("mission"))  return "#14b8a6"; // teal
  if (c.includes("incubator"))                       return "#f97316";
  if (c.includes("seed") || c.includes("agency"))    return "#8b5cf6";
  if (c.includes("portal") || c.includes("market"))  return "#64748b";
  return "#14b8a6";
}

// Simple popup markup built as a string (Leaflet requires raw HTML here);
// user interactions are re-bound via the `popupopen` event.
function buildPopup(o: MapOrg): string {
  const esc = (s: string) =>
    s.replace(/[&<>"']/g, (ch) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[ch]!));
  const cat = o.category ? `<div style="font-size:11px;color:#0f766e;font-weight:600;text-transform:uppercase;letter-spacing:.06em">${esc(o.category)}</div>` : "";
  const where = [o.state].filter(Boolean).map((x) => esc(x!)).join(" · ");
  const apply = o.application_url
    ? `<a href="${esc(o.application_url)}" target="_blank" rel="noopener noreferrer"
          style="display:inline-block;background:#0d9488;color:white;padding:4px 10px;border-radius:999px;font-size:12px;text-decoration:none;margin-right:6px">Visit portal →</a>`
    : "";
  const details = `<a href="#" data-open-scheme
        style="display:inline-block;border:1px solid #cbd5e1;color:#0f172a;padding:4px 10px;border-radius:999px;font-size:12px;text-decoration:none">Details</a>`;
  return `
    <div style="font-family:Inter,system-ui,sans-serif;line-height:1.3;min-width:220px">
      ${cat}
      <div style="font-weight:600;margin-top:2px;margin-bottom:2px;font-size:14px">${esc(o.name)}</div>
      <div style="color:#64748b;font-size:11px;margin-bottom:6px">${where}</div>
      ${o.summary ? `<div style="color:#334155;font-size:12px;margin-bottom:8px">${esc(o.summary.slice(0, 160))}${o.summary.length > 160 ? "…" : ""}</div>` : ""}
      ${apply}${details}
    </div>`;
}
